/**
 * `Slider` (W1-12 ui-kit-i18n; contract SliderProps): Radix slider with its label and formatted value. Dragging
 * or arrow keys report live values (`final` false: callers run a Gesture); releasing the pointer or finishing a
 * key press reports the final value once (`final` true). Arrows step, PageUp/PageDown step by 10, Home/End jump
 * to the ends; Shift+arrow steps by 10 as well. The thumb's aria-valuetext uses `format`.
 */

import * as RSl from '@radix-ui/react-slider';
import { useId } from 'react';
import { cx } from './internal/cx';
import f from './internal/form.module.css';
import s from './Slider.module.css';
import type { SliderProps } from './types';

export function Slider(props: SliderProps & { readonly className?: string | undefined }) {
  const labelId = useId();
  const text = props.format === undefined ? String(props.value) : props.format(props.value);
  return (
    <div className={cx(f.control, props.className)}>
      <div className={s.header}>
        <span id={labelId} className={f.label}>
          {props.label}
        </span>
        <output className={s.value} aria-hidden>
          {text}
        </output>
      </div>
      <RSl.Root
        className={s.root}
        value={[props.value]}
        min={props.min}
        max={props.max}
        step={props.step ?? 1}
        onValueChange={(v) => {
          const x = v[0];
          if (x !== undefined) props.onChange(x, false);
        }}
        onValueCommit={(v) => {
          // Radix reports a keyboard commit before the matching change; deliver `final` after it.
          const x = v[0];
          if (x !== undefined) {
            queueMicrotask(() => {
              props.onChange(x, true);
            });
          }
        }}
      >
        <RSl.Track className={s.track}>
          <RSl.Range className={s.range} />
        </RSl.Track>
        <RSl.Thumb className={s.thumb} aria-labelledby={labelId} aria-valuetext={text} />
      </RSl.Root>
    </div>
  );
}
